// ==================== Task Management Core Types & Models ====================

export type TaskStatus = 'In-Progress' | 'Pending' | 'Completed' | 'Cancelled';
export type PriorityLevel = 'Urgent' | 'High' | 'Normal' | 'Low';
export type AttachmentType = 'pdf' | 'image' | 'doc';

export interface TaskAttachment {
  id: string;
  name: string;
  type: AttachmentType;
}

export interface TaskNote {
  id: string;
  authorName: string;
  authorInitials: string;
  avatarColor?: string;
  createdDate: string;
  noteText: string;
  notePlain: string;
  attachments?: TaskAttachment[];
}

export enum TaskStage {
  ALL = 'All',
  INTAKE = 'Intake',
  PAPERWORK = 'Paperwork',
  PLAN_REVIEW = 'Plan Review',
  SUBMITTED_TO_CITY = 'Submitted To City',
  CITY_REVIEW = 'City Review',
  CITY_APPROVED = 'City Approved',
  PERMIT_ISSUED = 'Permit Issued',
  INSPECTIONS = 'Inspections',
  FINAL_CLOSURE = 'Final Closure'
}

export interface TaskItem {
  taskID: string;
  title: string;
  status: TaskStatus;
  stage?: TaskStage;
  stageID: StageEnum,
  hasGreenTick?: boolean;
  hasProgressIcon?: boolean;
  dueDate: string;
  isOverdue?: boolean;
  priority?: PriorityLevel;
  assignedUser?: {
    name: string;
    extraCount?: number;
    extraUsers?: string[];
  };
  description: string;
  attachments: TaskAttachment[];
  files?: any[];
  notes?: TaskNote[];
  isNotesExpanded?: boolean;
  createdBy?: {
    authorName: string;
    createdDate: string;
  };
  updatedBy?: {
    authorName: string;
    updatedDate: string;
  };
  completedByUserName?: string | null;
  completedOn?: string | null;
  startedOn?: string | null;
  elapsedMinutes?: number | string | null;
  updatedOn?: string | null;
  projectFlags?: {
    noc: boolean;
    ntbo: boolean;
    pp: boolean;
    coc?: boolean;
  };
  canEdit?: boolean;
  canDelete?: boolean;
  visibleToContractor?: boolean;
  projectID?: number | null;
}

export const getProjectFlagsForStage = (stage?: string) => {
  if (!stage) return [];
  const normalized = stage.toLowerCase().replace(/\s+/g, '');
  if (normalized === 'paperwork') {
    return [
      { key: 'noc', label: 'NOC' },
      { key: 'ntbo', label: 'NTBO' },
      { key: 'pp', label: 'PP' }
    ];
  }
  if (normalized === 'finalclosure' || normalized === 'finalclouser') {
    return [
      { key: 'coc', label: 'CoC' }
    ];
  }
  return [];
};

// ==================== Task Template Models ====================

export interface TaskTemplate {
  id: string;
  name: string;
}

// ==================== Task Management Drawer Component Interfaces ====================

export interface TaskManagementDrawerProps {
  open: boolean;
  onClose: () => void;
  stage?: TaskStage
  openTaskModal?: boolean
  selectedProjectID?: number;
}

// ==================== Task Card Component Interfaces ====================

export interface TaskCardProps {
  task: TaskItem;
  allowEdit?: boolean;
  allowDelete?: boolean;
  allowView?: boolean;
  allowAdd?: boolean;
  onView?: (task: TaskItem) => void;
  onEdit?: (task: TaskItem) => void;
  onDelete?: (task: TaskItem) => void;
  onAddNote?: (task: TaskItem) => void;
}

// ==================== Add / Edit Task Form Component Interfaces ====================

export interface TaskFormValues {
  title: string;
  dueDate: any;
  dueTime: any;
  priority: string;
  status: string;
  assignedTo: number | number[] | null;
  stage: string;
  visibleToContractor: boolean;
  projectFlags: {
    noc: boolean;
    ntbo: boolean;
    pp: boolean;
    coc: boolean;
  };
  description: string;
}

export interface TaskFormProps {
  open: boolean;
  closeModal: () => void;
  task?: TaskItem | any | null;
  projectName?: string;
  onSave?: (taskValues: TaskFormValues) => Promise<void> | void;
  stageName?: string | null;
}

// ==================== Add Note Form Component Interfaces ====================

export interface NoteFormValues {
  note: string;
}

export interface NoteFormProps {
  open: boolean;
  closeModal: () => void;
  taskId?: string;
  selectedNote?: TaskNote | null;
  onSave?: (values: NoteFormValues, files?: File[]) => Promise<void> | void;
}

// ==================== View Task Form Component Interfaces ====================

export interface ViewTaskFormProps {
  open: boolean;
  closeModal: () => void;
  task?: TaskItem | any | null;
  projectName?: string;
  onEdit?: (task: TaskItem) => void;
  onNoteChange?: () => void;
}

// ==================== Delete Task Modal Component Interfaces ====================

export interface DeleteTaskModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  taskTitle?: string;
  filesCount?: number;
  loading?: boolean;
}

// ==================== Delete Note Modal Component Interfaces ====================

export interface DeleteNoteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void | Promise<void>;
  filesCount?: number;
  loading?: boolean;
}

// ==================== Cancel Task Modal Component Interfaces ====================

export interface CancelModalProps {
  open: boolean;
  onClose: () => void;
  taskTitle?: string;
  taskStatus?: string | number;
  taskId?: number | string;
  updatedOn?: string | null;
  onSuccess?: (res?: any) => void;
  path?: string;
}


// ==================== Task Filter Interfaces ====================

export interface TaskFilters {
  taskModule?: number | null;
  projectID?: number | null;
  status?: string | number | null;
  priority?: string | number | null;
  assignedUserIDs?: (number | string)[] | null;
  dueDateFrom?: string | null;
  dueDateTo?: string | null;
  createdDateFrom?: string | null;
  createdDateTo?: string | null;
}

// ==================== Update Task Status Payload Interface ====================

export interface UpdateTaskStatusPayload {
  updatedOn?: string | null;
  taskID: number;
  taskStatus: number;
  reasonToCancel?: string | null;
}

// ==================== Update Task DueOn Payload Interface ====================

export interface UpdateDueOnPayload {
  updatedOn?: string | null;
  taskID: number;
  DueOn: string;
}

export type UpdateTaskDueOnPayload = UpdateDueOnPayload;

// ==================== Update Elapsed Minutes (TAT) Payload Interface ====================

export interface UpdateElapsedMinutesPayload {
  updatedOn?: string | null;
  taskID: number;
  startedOn: string | null;
  completedOn: string | null;
}

// ==================== Task File Management API Interfaces ====================

export interface GenerateUploadFileUrlsPayload {
  taskID: number;
  fileNames: string[];
}

export interface GenerateBlobFileIDPayload {
  taskID: number;
  fileName: string;
}

export interface GetFileUrlParams {
  taskID: number | string;
  fileID?: string;
  fileName?: string;
}

export interface DeleteTaskFilePayload {
  taskID: number | string;
  fileID: string;
}

// ==================== Task Management Enums ====================

export enum TaskPriorityEnum {
  Urgent = 0,
  High = 1,
  Normal = 2,
  Low = 3
}

export enum TaskModuleEnum {
  General = 0,
  Project = 1,
  PlanReview = 2,
  Inspection = 3
}

export enum TaskStatusEnum {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Cancelled = 3
}

export enum StageStatusEnum {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Cancelled = 3
}

export enum StageEnum {
  Intake = 1,
  Paperwork = 2,
  PlanReview = 3,
  SubmittedToCity = 4,
  CityReview = 5,
  CityApproved = 6,
  PermitIssued = 7,
  Inspections = 8,
  FinalClosure = 9
}

export enum StageFilterEnum {
  General = 0,
  Intake = StageEnum.Intake,
  Paperwork = StageEnum.Paperwork,
  PlanReview = StageEnum.PlanReview,
  SubmittedToCity = StageEnum.SubmittedToCity,
  CityReview = StageEnum.CityReview,
  CityApproved = StageEnum.CityApproved,
  PermitIssued = StageEnum.PermitIssued,
  Inspections = StageEnum.Inspections,
  FinalClosure = StageEnum.FinalClosure
}

export const getStageFilterName = (val: any): string => {
  const num = Number(val);
  if (num === StageFilterEnum.General) return 'General';
  if (num === StageFilterEnum.Intake) return 'Intake';
  if (num === StageFilterEnum.Paperwork) return 'Paperwork';
  if (num === StageFilterEnum.PlanReview) return 'Plan Review';
  if (num === StageFilterEnum.SubmittedToCity) return 'Submitted to City';
  if (num === StageFilterEnum.CityReview) return 'City Review';
  if (num === StageFilterEnum.CityApproved) return 'City Approved';
  if (num === StageFilterEnum.PermitIssued) return 'Permit Issued';
  if (num === StageFilterEnum.Inspections) return 'Inspections';
  if (num === StageFilterEnum.FinalClosure) return 'Final Closure';
  return typeof val === 'string' ? val : '';
};

export const getTaskModuleName = (val: any): string => {
  const num = Number(val);
  if (num === TaskModuleEnum.General || String(val).toLowerCase() === 'general') return 'General';
  if (num === TaskModuleEnum.Project || String(val).toLowerCase() === 'project') return 'Project';
  if (num === TaskModuleEnum.PlanReview || String(val).toLowerCase().replace(/\s+/g, '') === 'planreview') return 'Plan Review';
  if (num === TaskModuleEnum.Inspection || String(val).toLowerCase().includes('inspection')) return 'Inspection';
  return typeof val === 'string' ? val : 'General';
};

export const getPriorityName = (val: any): string => {
  if (val === 0 || val === '0' || val === TaskPriorityEnum.Urgent) return 'Urgent';
  if (val === 1 || val === '1' || val === TaskPriorityEnum.High) return 'High';
  if (val === 2 || val === '2' || val === TaskPriorityEnum.Normal) return 'Normal';
  if (val === 3 || val === '3' || val === TaskPriorityEnum.Low) return 'Low';
  return typeof val === 'string' ? val : 'Normal';
};

export const getStatusName = (val: any): string => {

  if (val === 0 || val === '0' || val === TaskStatusEnum.Pending) return 'Pending';
  if (val === 1 || val === '1' || val === TaskStatusEnum.InProgress) return 'In-Progress';
  if (val === 2 || val === '2' || val === TaskStatusEnum.Completed) return 'Completed';
  if (val === 3 || val === '3' || val === TaskStatusEnum.Cancelled) return 'Cancelled';
  const str = String(val || '').trim().toLowerCase();
  if (str.includes('in progress') || str.includes('in-progress')) return 'In-Progress';
  if (str.includes('pending')) return 'Pending';
  if (str.includes('completed')) return 'Completed';
  if (str.includes('cancelled')) return 'Cancelled';
  return typeof val === 'string' ? val : 'Pending';
};

export const TASK_STAGES = {
  1: TaskStage.INTAKE,
  2: TaskStage.PAPERWORK,
  3: TaskStage.PLAN_REVIEW,
  4: TaskStage.SUBMITTED_TO_CITY,
  5: TaskStage.CITY_REVIEW,
  6: TaskStage.CITY_APPROVED,
  7: TaskStage.PERMIT_ISSUED,
  8: TaskStage.INSPECTIONS,
  9: TaskStage.FINAL_CLOSURE
} as const;

export const getStageNameFromId = (id: any): TaskStage | null => {
  const numericId = Number(id);
  if (numericId === 1) return TaskStage.INTAKE;
  if (numericId === 2) return TaskStage.PAPERWORK;
  if (numericId === 3) return TaskStage.PLAN_REVIEW;
  if (numericId === 4) return TaskStage.SUBMITTED_TO_CITY;
  if (numericId === 5) return TaskStage.CITY_REVIEW;
  if (numericId === 6) return TaskStage.CITY_APPROVED;
  if (numericId === 7) return TaskStage.PERMIT_ISSUED;
  if (numericId === 8) return TaskStage.INSPECTIONS;
  if (numericId === 9) return TaskStage.FINAL_CLOSURE;
  return null;
};

export const getStageId = (stage: string): number | null => {
  switch (stage) {
    case 'General':
    case 'general':
      return 0;
    case TaskStage.INTAKE:
      return 1;
    case TaskStage.PAPERWORK:
      return 2;
    case TaskStage.PLAN_REVIEW:
      return 3;
    case TaskStage.SUBMITTED_TO_CITY:
      return 4;
    case TaskStage.CITY_REVIEW:
      return 5;
    case TaskStage.CITY_APPROVED:
      return 6;
    case TaskStage.PERMIT_ISSUED:
      return 7;
    case TaskStage.INSPECTIONS:
    case 'Inspection':
      return 8;
    case TaskStage.FINAL_CLOSURE:
      return 9;
    default:
      return null;
  }
};

export const getPriorityEnumVal = (priority: string): number => {
  switch (priority) {
    case 'Urgent':
      return TaskPriorityEnum.Urgent;
    case 'High':
      return TaskPriorityEnum.High;
    case 'Normal':
      return TaskPriorityEnum.Normal;
    case 'Low':
      return TaskPriorityEnum.Low;
    default:
      return TaskPriorityEnum.Normal;
  }
};

export const getStatusEnumVal = (status: any): number => {
  if (status === 0 || status === '0' || status === TaskStatusEnum.Pending) return TaskStatusEnum.Pending;
  if (status === 1 || status === '1' || status === TaskStatusEnum.InProgress) return TaskStatusEnum.InProgress;
  if (status === 2 || status === '2' || status === TaskStatusEnum.Completed) return TaskStatusEnum.Completed;
  if (status === 3 || status === '3' || status === TaskStatusEnum.Cancelled) return TaskStatusEnum.Cancelled;
  const str = String(status || '').trim().toLowerCase();
  if (str.includes('in progress') || str.includes('in-progress')) return TaskStatusEnum.InProgress;
  if (str.includes('pending')) return TaskStatusEnum.Pending;
  if (str.includes('completed')) return TaskStatusEnum.Completed;
  if (str.includes('cancelled')) return TaskStatusEnum.Cancelled;
  return TaskStatusEnum.Pending;
};

export interface ChangeDueDateFormProps {
  open: boolean;
  onClose: () => void;
  taskData?: any;
  taskId?: number | string;
  taskTitle?: string;
  currentDueDate?: string;
  assignees?: { name: string; avatarColor?: string }[] | any[];
  extraAssigneesCount?: number;
  projectName?: string;
  categoryName?: string;
  stageName?: string;
  onSuccess?: () => void;
  onSubmit?: (updatedValues: { dueDate: any; dueTime: any }) => Promise<void> | void;
}

export interface GetTaskBoardPayload {
  specialistIDs?: (number | string)[];
  projectIDs?: (number | string)[];
  contractorIDs?: (number | string)[];
  stageIDs?: (number | string)[];
  statusIDs?: (number | string)[];
  moduleIDs?: (number | string)[];
  priorityIDs?: (number | string)[];
  assignedUserIDs?: (number | string)[];
  parentCompanyIDs?: (number | string)[];
  dueFromDate?: string | null;
  dueToDate?: string | null;
  createdDateFrom?: string | null;
  createdDateTo?: string | null;
}

// ==================== Task Board API Response Interfaces ====================

// User assigned to a task in TaskBoard API
export interface TaskBoardAssignedUser {
  taskID: number;
  userID: number;
  userFullName: string;
  userType?: string | null;
}

// Single task object returned by useGetTaskBoardList API
export interface TaskBoardApiTask {
  taskID: number;
  taskTitle: string;
  taskStatus: number; // TaskStatusEnum (0: Pending, 1: InProgress, 2: Completed, 3: Cancelled)
  taskStatusName: string;
  taskPriority: number; // TaskPriorityEnum (0: Urgent, 1: High, 2: Normal, 3: Low)
  taskPriorityName: string;
  dueOn: string | null;
  createdOn?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  projectName?: string | null;
  taskModuleName?: string | null;
  stageID?: number | null;
  stageName?: string | null;
  noc: boolean;
  ntbo: boolean;
  pp: boolean;
  coC: boolean;
  totalAttachmentCount: number;
  canEdit: boolean;
  canDelete: boolean;
  canChangeStatus: boolean;
  assignedUsers: TaskBoardAssignedUser[];
  projectID?: number | null;
  projectId?: number | null;
  municipalityName?: string | null;
  specialistID?: number | null;
  specialistName?: string | null;
  roleType?: string | null;
  specialists?: { specialistID?: number; name?: string; specialistName?: string; roleType?: string }[] | null;
  contractorID?: number | null;
  contractorName?: string | null;
  contractorCompanyName?: string | null;
  contractorPhone?: string | null;
  contractorEmail?: string | null;
  taskModuleID?: number | null;
  moduleId?: number | null;
  createdByUserName?: string | null;
  updatedByUserName?: string | null;
  updatedOn?: string | null;
  completedByUserName?: string | null;
  completedOn?: string | null;
  startedOn?: string | null;
  elapsedMinutes?: number | null;
}

// Project lookup object returned by useGetTaskBoardList API
export interface TaskBoardApiProject {
  projectID: number;
  projectName: string;
  contractorCompanyID?: number | null;
  specialistID?: number | null;
  specialistName?: string | null;
  specialistUserType?: string | null;
  municipalityName?: string | null;
  startedOn?: string | null;
  completedOn?: string | null;
  elapsedMinutes?: number | null;
}

// Contractor lookup object returned by useGetTaskBoardList API
export interface TaskBoardApiContractor {
  contractorCompanyID: number;
  contractorCompanyName: string;
  emailID?: string | null;
  phoneNo?: string | null;
  parentCompanyName?: string | null;
}

// Response structure returned by useGetTaskBoardList API
export interface TaskBoardListResponse {
  totalTaskCount: number;
  tasks: TaskBoardApiTask[];
  projects?: TaskBoardApiProject[];
  contractors?: TaskBoardApiContractor[];
}








